/**
 * BIM Modeler — service layer for the 3D modeler tool (docs/MODELER.md,
 * docs/bim-plan/02-integration.md). Backed by api/bim-modeler.js (Supabase `bim_models` +
 * the public `bim-models` bucket). The ONLY caller of that endpoint — components and the
 * modeler page never fetch directly (Data Access Rules).
 *
 * Internal team tool only (Clerk-authed everywhere; no public/token path).
 * Snapshot bytes are raw binary Yjs updates (parameters only, never geometry).
 */

async function authHeaders() {
  const token = await window.__clerkGetToken()
  return { 'Content-Type': 'application/json', 'Authorization': 'Bearer ' + token }
}

async function call(method, body, qs) {
  const res = await fetch('/api/bim-modeler' + (qs ? '?' + qs : ''), {
    method,
    headers: await authHeaders(),
    body: body ? JSON.stringify(body) : undefined,
  })
  const data = await res.json().catch(() => ({}))
  if (!res.ok) {
    const err = new Error(data.error || ('Modeler request failed (' + res.status + ')'))
    err.status = res.status
    throw err
  }
  return data
}

/** Idempotent create-or-open for a Concept's model. -> model shape (see api). */
export async function getOrCreateModel(conceptId, projectId) {
  const { model } = await call('POST', { action: 'getOrCreate', conceptId, projectId })
  return model
}

/**
 * SHEET LINK SYNC: the AlderCAD sheets linked to a Cortex Drawing Sheet across the project's
 * models, with how much is drawn on each, plus the concept names. Throws when the check could
 * not run — callers must treat that as "unknown", never as "no AlderCAD work".
 * @returns {Promise<{ conceptName: string, sheetNumber: string, sheetName: string, viewports: number, schedules: number, pdfInsert: boolean, unreadable?: boolean }[]>}
 */
export async function getSheetUsageInModels(projectId, airtableSheetId) {
  const { uses } = await call('POST', { action: 'sheetUsage', projectId, airtableSheetId })
  if (!uses?.length) return []
  let names = new Map()
  try {
    const { getConcepts } = await import('./design.js')
    names = new Map((await getConcepts(projectId)).map((c) => [c.id, c.fields?.['Parti Pris Title'] || '']))
  } catch { /* names are a nicety */ }
  return uses.map((u) => ({ ...u, conceptName: names.get(u.conceptId) || 'a concept' }))
}

/**
 * My persistent avatar color (AVATAR IDENTITY round, database/181) — assigned once
 * from the server-side palette in join order, kept across every model. Returns null
 * on any failure (callers fall back to the peerColor hash — never blocks the open).
 */
export async function getMyAvatarColor() {
  try {
    const { color } = await call('POST', { action: 'avatarColor' })
    return color || null
  } catch {
    return null
  }
}

/** Fetch the current snapshot bytes; null when the model has never been saved. */
export async function loadSnapshot(model) {
  if (!model || !model.snapshotUrl) return null
  // cache-bust: the public URL is stable per path, but the path changes per save
  const res = await fetch(model.snapshotUrl)
  if (!res.ok) throw new Error('Snapshot download failed (' + res.status + ')')
  return new Uint8Array(await res.arrayBuffer())
}

/**
 * SAVE MERGE round: after a save conflict — the current model row (to merge its
 * snapshot from) and whether a RESTORE landed since `sinceSeq`
 * (true or null = the caller must reload, never merge).
 */
export async function getConflictState(modelId, sinceSeq) {
  return call('POST', { action: 'conflictState', modelId, sinceSeq })
}

/**
 * Save a snapshot via the signed-URL path (the normal autosave). Throws with
 * `.status === 409` on a sequence conflict (saved elsewhere — caller must reload).
 * Returns the updated model (carries the new snapshotSeq).
 *
 * `userName` (Phase 4) is stamped onto the history ledger row as-is — the caller's
 * own already-known display name (e.g. Clerk's `user.fullName`), not re-derived here.
 */
export async function saveSnapshot(model, bytes, userName) {
  const { signedUrl, path, seq } = await call('POST', { action: 'saveUrl', modelId: model.id })
  const put = await fetch(signedUrl, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/octet-stream', 'x-upsert': 'true' },
    body: bytes,
  })
  if (!put.ok) throw new Error('Snapshot upload failed (' + put.status + ')')
  const { model: updated } = await call('POST', { action: 'commitSave', modelId: model.id, path, seq, userName })
  return updated
}

/**
 * Upload a DXF underlay file for a model (docs/MODELER.md DXF underlay round).
 * Returns { path, url } — the doc's settings record stores both; the file itself
 * lives in Storage under dxf/<modelId>/ and is never embedded in the model doc.
 */
export async function uploadDxfUnderlay(modelId, file) {
  const { signedUrl, path, url } = await call('POST', {
    action: 'underlayUploadUrl', modelId, filename: file.name,
  })
  const put = await fetch(signedUrl, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/octet-stream', 'x-upsert': 'true' },
    body: file,
  })
  if (!put.ok) throw new Error('DXF upload failed (' + put.status + ')')
  return { path, url }
}

/** Best-effort removal of a detached DXF underlay file. Never throws. */
export function deleteDxfUnderlay(modelId, path) {
  call('POST', { action: 'underlayDelete', modelId, path }).catch(() => {})
}

/**
 * Upload a raster PDF underlay file for a model (docs/MODELER.md PDF underlay round —
 * the DXF underlay's sibling for a reference file that's a PDF export, not a DXF).
 * Returns { path, url } — the file lives in Storage under pdf/<modelId>/ and is never
 * embedded in the model doc.
 */
export async function uploadPdfUnderlay(modelId, file) {
  const { signedUrl, path, url } = await call('POST', {
    action: 'pdfUnderlayUploadUrl', modelId, filename: file.name,
  })
  const put = await fetch(signedUrl, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/octet-stream', 'x-upsert': 'true' },
    body: file,
  })
  if (!put.ok) throw new Error('PDF upload failed (' + put.status + ')')
  return { path, url }
}

/** Best-effort removal of a detached PDF underlay file. Never throws. */
export function deletePdfUnderlay(modelId, path) {
  call('POST', { action: 'pdfUnderlayDelete', modelId, path }).catch(() => {})
}

/**
 * Upload a plain image underlay file for a model (docs/MODELER.md IMAGE UNDERLAY
 * round — the PDF underlay's sibling for a reference file that's a plain image, not a
 * PDF or a DXF). Returns { path, url } — the file lives in Storage under
 * image/<modelId>/ and is never embedded in the model doc.
 */
export async function uploadImageUnderlay(modelId, file) {
  const { signedUrl, path, url } = await call('POST', {
    action: 'imageUnderlayUploadUrl', modelId, filename: file.name,
  })
  const put = await fetch(signedUrl, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/octet-stream', 'x-upsert': 'true' },
    body: file,
  })
  if (!put.ok) throw new Error('Image upload failed (' + put.status + ')')
  return { path, url }
}

/** Best-effort removal of a detached image underlay file. Never throws. */
export function deleteImageUnderlay(modelId, path) {
  call('POST', { action: 'imageUnderlayDelete', modelId, path }).catch(() => {})
}

/**
 * List this model's snapshot history ledger (SNAPSHOT HISTORY round, database/184),
 * most recent first. Each entry: { id, seq, size_bytes, created_at, created_by_clerk_id,
 * created_by_name, retention_tier, label }. `created_by_name` is null for rows saved
 * before Phase 4 (database/185) or by a caller that omitted it — fall back to a short
 * form of `created_by_clerk_id` in the UI.
 */
export async function listSnapshotHistory(modelId) {
  const { snapshots } = await call('POST', { action: 'listHistory', modelId })
  return snapshots
}

/**
 * NAMED VERSIONS (2026-11-03): name a saved version so the daily cleanup never thins it.
 * An empty name removes it. Returns the updated history row.
 */
export async function labelSnapshot(modelId, snapshotId, label) {
  const { snapshot } = await call('POST', { action: 'labelSnapshot', modelId, snapshotId, label })
  return snapshot
}

/**
 * Restore an earlier snapshot as the new current version (Phase 3). Just another
 * commit through the normal optimistic-concurrency path server-side — the restored
 * snapshot becomes its own new history entry, so restoring-from-a-restore needs no
 * special handling here either. Throws with `.status === 409` if someone saved
 * concurrently. Returns the updated model (new snapshotSeq/snapshotUrl).
 *
 * Callers MUST also broadcast the restore to any other live session in this model
 * (`syncRef.current?.restoreAll(name)`, frontend/src/bim/sync.js) — this call alone
 * only updates the server; it has no way to know who else has the model open.
 * `userName` (Phase 4) is stamped onto the new ledger row exactly like `saveSnapshot`.
 */
export async function restoreSnapshot(modelId, snapshotId, userName) {
  const { model } = await call('POST', { action: 'restoreSnapshot', modelId, snapshotId, userName })
  return model
}

/**
 * TAG ORIGINS + ADJUSTMENT LOG (2026-10-15, database/301): append hand tag moves/resets to
 * the adjustment log. Fire-and-forget — never throws, never blocks the drawing.
 */
export function logTagAdjustments(modelId, conceptId, isSandbox, userName, rows) {
  if (!modelId || !rows?.length) return
  call('POST', { action: 'logTagAdjustments', modelId, conceptId, isSandbox: !!isSandbox, userName, rows })
    .catch((e) => console.warn('tag adjustment log failed:', e.message))
}

/** The Moved Tags… history: { model: { total, byFamily }, firm: { total, byVersion, byMonth } }. */
export async function getTagAdjustmentSummary(modelId) {
  return call('POST', { action: 'tagAdjustmentSummary', modelId })
}

/**
 * Best-effort pagehide save (docs/bim-plan/02 §3): a keepalive POST that survives tab
 * close, possible ONLY for small docs (keepalive bodies cap at ~64KB). Returns true if
 * the request was dispatched; false when the doc is too large (the idle autosave is the
 * durability mechanism then). Never throws — this runs during unload.
 */
export function saveInlineKeepalive(model, bytes, clerkToken, userName) {
  try {
    if (!model || !bytes || bytes.byteLength > 58_000) return false
    let bin = ''
    for (let i = 0; i < bytes.length; i++) bin += String.fromCharCode(bytes[i])
    const snapshotBase64 = btoa(bin)
    fetch('/api/bim-modeler', {
      method: 'POST',
      keepalive: true,
      headers: { 'Content-Type': 'application/json', 'Authorization': 'Bearer ' + clerkToken },
      body: JSON.stringify({
        action: 'saveInline',
        modelId: model.id,
        seq: model.snapshotSeq + 1,
        snapshotBase64,
        userName,
      }),
    }).catch(() => {})
    return true
  } catch {
    return false
  }
}
